import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { ProductComparison } from "@/components/ProductComparison";
import { HERO_PICKS } from "@/lib/hero-picks";

export const Route = createFileRoute("/vertailu")({
  head: () => ({
    meta: [
      { title: "Tuotevertailu: mikä Montessori-tuote lapselle? | Montessori24" },
      {
        name: "description",
        content:
          "Vertaa neljää suositelluinta tuotettamme: Pikler-kiipeilykolmio, kolmio ja ramppi, keittiötorni ja pehmoleikkisetti. Ikä, hinta, tilantarve ja materiaalit rinnakkain.",
      },
      { property: "og:title", content: "Tuotevertailu: mikä Montessori-tuote lapselle?" },
      {
        property: "og:description",
        content:
          "Neljä suositeltua tuotetta rinnakkain: ikäsuositus, hinta, tilantarve ja käyttöikä. Valitse yksi minuutissa.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://montessori24.fi/vertailu" },
      { property: "og:image", content: "https://montessori24.fi/og-image.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Tuotevertailu: mikä Montessori-tuote lapselle?" },
      {
        name: "twitter:description",
        content: "Neljä suositeltua tuotetta rinnakkain: ikä, hinta, tilantarve ja käyttöikä.",
      },
      { name: "twitter:image", content: "https://montessori24.fi/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://montessori24.fi/vertailu" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Etusivu", item: "https://montessori24.fi/" },
            { "@type": "ListItem", position: 2, name: "Tuotevertailu", item: "https://montessori24.fi/vertailu" },
          ],
        }),
      },
    ],
  }),
  component: VertailuPage,
});

function VertailuPage() {
  return (
    <Layout>
      <section className="border-b border-border bg-cream">
        <div className="container-prose px-4 pt-16 pb-10 md:px-6 md:pt-24 md:pb-14">
          <nav aria-label="Murupolku" className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
            <Link to="/" className="hover:text-foreground">
              Etusivu
            </Link>
            <span aria-hidden> / </span>
            <span className="text-foreground">Vertailu</span>
          </nav>
          <p className="mt-6 text-[11px] font-medium uppercase tracking-[0.24em] text-clay">
            Valitse yksi — ei kymmentä
          </p>
          <h1 className="mt-3 max-w-3xl font-display text-4xl italic text-foreground md:text-6xl text-balance">
            Neljä tuotetta, joita todella suosittelemme.
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            Mallistossamme on kymmeniä tuotteita, mutta lähes jokaiselle perheelle sopii yksi
            näistä neljästä. Alla ne rinnakkain: ikäsuositus, hinta, tilantarve ja käyttöikä —
            vain vahvistetut tiedot, ei arvauksia.
          </p>
        </div>
      </section>

      <section className="bg-background" aria-labelledby="vertailu-taulukko">
        <div className="container-prose px-4 py-14 md:px-6 md:py-20">
          <h2 id="vertailu-taulukko" className="sr-only">
            Tuotteiden vertailutaulukko
          </h2>
          <ProductComparison />
        </div>
      </section>

      <section className="border-t border-border bg-card">
        <div className="container-prose px-4 py-16 md:px-6 md:py-24">
          <h2 className="font-display text-3xl italic text-foreground md:text-4xl">
            Kumpi meille sopii?
          </h2>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {HERO_PICKS.map((p) => (
              <div key={p.handle} className="rounded-2xl border border-border bg-cream p-6">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-clay">
                  {p.bestFor}
                </p>
                <h3 className="mt-2 font-display text-xl italic text-foreground">{p.displayTitle}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.pickIf}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/kauppa"
              className="inline-flex items-center gap-2 rounded-[12px] border border-foreground/25 px-6 py-3 text-sm font-medium text-foreground transition-colors hover:border-foreground/55"
            >
              Koko mallisto <span aria-hidden>→</span>
            </Link>
            <Link
              to="/montessori-koulu/pikler-kiipeilyteline-opas"
              className="inline-flex items-center gap-2 rounded-[12px] border border-foreground/25 px-6 py-3 text-sm font-medium text-foreground transition-colors hover:border-foreground/55"
            >
              Lue Pikler-opas <span aria-hidden>→</span>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
